const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/sagas/nativePort.ts';
import { channel, eventChannel, SagaIterator } from 'redux-saga';
import Either from 'data.either';
import { all, call, fork, put, take } from 'redux-saga/effects';
import {
    ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_PENDING,
    ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_REFRESH,
    ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_REMOVE_TOKEN,
    ACTION_TYPE_NATIVE_PATAPP_PDF_SHARE_PENDING,
} from 'App/actions/patAppNative';
import { REQUEST_ACTION_TYPES_GET_PATAPP_QRCODE } from 'App/actions/nativeAppQRCodeScanner';

// FIXME: duplication in CameraOverlay / nativeCommUtils
declare global {
    interface Window {
        ReactNativeWebView: {
            postMessage(message: string): void;
        };
    }
}

// just extract some commonly used code for all native communication functions
function toNativeListenerWrapper(fn: (c: any) => SagaIterator) {
    return function* (chan: any): SagaIterator {
        while (true) {
            const payload = yield call(fn, chan);
            yield put(chan, payload);
        }
    };
}

function* nativePatappAuthorize(): SagaIterator {
    return yield take([
        ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_PENDING,
        ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_REFRESH,
        ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_REMOVE_TOKEN,
    ]);
}

function* nativePatappPdfShare(): SagaIterator {
    return yield take([ACTION_TYPE_NATIVE_PATAPP_PDF_SHARE_PENDING]);
}

function* nativePatappQRCode(): SagaIterator {
    return yield take([REQUEST_ACTION_TYPES_GET_PATAPP_QRCODE.pending]);
}

// collection of all functions that can trigger messages to native
const nativeWhiteList = [nativePatappAuthorize, nativePatappPdfShare, nativePatappQRCode].map(toNativeListenerWrapper);

// This function establishes a connection to the incoming message channel and
// forwards the incoming objects on an async channel
function connectNative() {
    return eventChannel((emitter: any) => {
        const nativeMsgHandler = (nativeEvent: MessageEvent) => {
            Either.try(JSON.parse)(nativeEvent.data).map<object>(emitter);
        };

        // There is still a slight different between the webview on android and ios
        // ios sends messages on window, android on document
        // just use both for now, it's not clear if there could arise problems
        // at this initialization point due to the fact that the correct os information
        // will arrive asynchronly from the native side.
        window.addEventListener('message', nativeMsgHandler, false);
        document.addEventListener('message', nativeMsgHandler, false);
        return () => {
            window.removeEventListener('message', nativeMsgHandler, false);
            document.removeEventListener('message', nativeMsgHandler, false);
        };
    });
}

// This function just connects to the channel on which the messages from the native side
// arrive and injects them into the redux cycle
function* fromNative(): SagaIterator {
    const chan = yield call(connectNative);
    try {
        while (true) {
            const msg = yield take(chan);
            // msg seems to come already parsed ...
            yield put(msg);
        }
    } finally {
        // console.log('channel to native was closed ...');
    }
}

// maybe useless, but still nice for logging. Probably wouldn't need to handle
// it over channel. Simple function call should be enough
function toNative() {
    return function* (chan: any): SagaIterator {
        while (true) {
            const action = yield take(chan);
            window.ReactNativeWebView.postMessage(JSON.stringify(action));
        }
    };
}

// start communication channel to native and all desired trigger functions
export default function* (): SagaIterator {
    yield fork(fromNative);
    const chan = yield call(channel);
    yield all([toNative()].concat(nativeWhiteList as any).map(fn => fork(fn, chan)));
}
